import React, {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

/**
 * This enhanced HydrationContext solves the issue with state updates interrupting
 * Suspense boundary hydration.
 *
 * The problem:
 * - React Suspense boundaries hydrate independently
 * - State updates in parent components can happen before Suspense children are fully hydrated
 * - This can cause hydration mismatches and content flickering
 *
 * The solution:
 * - Track both global hydration and individual component hydration
 * - Only consider the app fully hydrated when ALL registered components are hydrated
 * - Defer state updates until everything is hydrated
 */

interface HydrationContextType {
  isGloballyHydrated: boolean;
  areAllRegisteredComponentsHydrated: () => boolean;
  notifyHydrationChange: () => void;
}

const defaultContext: HydrationContextType = {
  isGloballyHydrated: false,
  areAllRegisteredComponentsHydrated: () => false,
  notifyHydrationChange: () => {},
};

// Create module-level sets to track components across provider remounts
// and to handle registrations that occur before the provider mounts
const _registeredComponents = new Set<string>();
const _hydratedComponents = new Set<string>();

// Module-level callback that will be set by the provider
let _notifyHydrationChange: (() => void) | null = null;

// Function to register a component for hydration tracking
export function registerComponent(id: string): void {
  if (!_registeredComponents.has(id)) {
    _registeredComponents.add(id);
  }
}

const _mountedComponents = new Map<string, number>();

export function markComponentMounted(id: string): void {
  _mountedComponents.set(id, (_mountedComponents.get(id) ?? 0) + 1);
}

export function markComponentUnmounted(id: string): void {
  const count = (_mountedComponents.get(id) ?? 0) - 1;
  if (count > 0) {
    _mountedComponents.set(id, count);
  } else {
    _mountedComponents.delete(id);
  }
}

// Function to mark a component as hydrated
export function markComponentAsHydrated(id: string): void {
  if (!_hydratedComponents.has(id)) {
    _hydratedComponents.add(id);
    if (_notifyHydrationChange) {
      setTimeout(() => {
        _notifyHydrationChange?.();
      }, 0);
    }
  }
}

const HydrationContext = createContext<HydrationContextType>(defaultContext);

export const HydrationProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [isGloballyHydrated, setIsGloballyHydrated] = useState(false);
  // Add a hydration version counter to trigger focused updates
  const [, setHydrationVersion] = useState(0);

  // Set hydrated state on client-side only
  useEffect(() => {
    startTransition(() => {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setIsGloballyHydrated(true);
    });
  }, []);

  const notifyHydrationChange = useCallback(() => {
    startTransition(() => {
      setHydrationVersion(prev => prev + 1);
    });
  }, []);

  useEffect(() => {
    _notifyHydrationChange = notifyHydrationChange;
    return () => {
      _notifyHydrationChange = null;
    };
  }, [notifyHydrationChange]);

  const areAllRegisteredComponentsHydrated = useCallback(() => {
    // If no components are registered, return the global hydration state
    if (_registeredComponents.size === 0) {
      return isGloballyHydrated;
    }

    // Check if all registered components are hydrated
    return Array.from(_registeredComponents).every(id =>
      _hydratedComponents.has(id),
    );
  }, [isGloballyHydrated]);

  const contextValue = {
    isGloballyHydrated,
    areAllRegisteredComponentsHydrated,
    notifyHydrationChange,
  };

  return (
    <HydrationContext.Provider value={contextValue}>
      {children}
    </HydrationContext.Provider>
  );
};

let isHydrationDone = false;

const useHydrationContext = () => {
  return useContext(HydrationContext);
};

const checkHydrationStatus = (ctx: HydrationContextType): boolean => {
  const allComponentsHydrated = ctx.areAllRegisteredComponentsHydrated();
  return ctx.isGloballyHydrated && allComponentsHydrated;
};

/**
 * Hook to check if the entire application is hydrated.
 * This includes both the main app and all lazy-loaded components.
 * Use this to defer state updates until everything is hydrated.
 */
export const useHydrationDone = (): boolean => {
  const ctx = useHydrationContext();
  const [done, setDone] = useState(isHydrationDone);

  useEffect(() => {
    if (isHydrationDone || done) return;

    if (checkHydrationStatus(ctx)) {
      isHydrationDone = true;
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setDone(true);
    }
  }, [ctx, done]);

  if (isHydrationDone || done) {
    return true;
  }

  const currentHydrationDone = checkHydrationStatus(ctx);
  if (currentHydrationDone) {
    isHydrationDone = true;
    queueMicrotask(() => setDone(true));
  }

  return isHydrationDone || done;
};

/**
 * A snapshot of the hydration tracking for diagnostics. `orphanedIds` are
 * pending components that are no longer mounted, so nothing can mark them as
 * hydrated anymore.
 */
export const getHydrationState = () => {
  const pendingIds = Array.from(_registeredComponents).filter(
    id => !_hydratedComponents.has(id),
  );

  return {
    isHydrationDone,
    pendingIds,
    orphanedIds: pendingIds.filter(id => !_mountedComponents.has(id)),
  };
};
